import { useState } from "react";
import { showToast } from "@/app/components/ui/Toast";
import { workspacesApi, uploadApi } from "@/lib/api";
import {
    DEFAULT_WORKSPACE_AVATAR,
    resolveWorkspaceImageUrlForCreate,
    type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";
import { syncWorkspaceAvatarTags } from "@/lib/workspaceAvatarTags";

interface UseWorkspaceCreationProps {
    onSuccess?: (workspaceId?: string) => void;
    onError?: (error: string) => void;
}

export function useWorkspaceCreation({ onSuccess, onError }: UseWorkspaceCreationProps = {}) {
    const [isCreating, setIsCreating] = useState(true);
    const [newWorkspaceName, setNewWorkspaceName] = useState("");
    const [newStoreId, setNewStoreId] = useState("");
    const [creatingLoading, setCreatingLoading] = useState(false);
    const [avatar, setAvatar] = useState<WorkspaceAvatarSelection>(DEFAULT_WORKSPACE_AVATAR);
    const [uploadingImage, setUploadingImage] = useState(false);

    const resetForm = () => {
        setNewWorkspaceName("");
        setNewStoreId("");
        setAvatar(DEFAULT_WORKSPACE_AVATAR);
        setIsCreating(true);
    };

    const handleCreateWorkspace = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newWorkspaceName.trim()) return;

        try {
            setCreatingLoading(true);
            let imageUrl: string | undefined;

            if (avatar.mode === "image" && avatar.file) {
                setUploadingImage(true);
                try {
                    const uploadRes = await uploadApi.uploadImage(avatar.file, undefined, "thumbnails");
                    imageUrl =
                        uploadRes.url ||
                        uploadRes.imageUrl ||
                        uploadRes.file?.url ||
                        undefined;
                } catch (uploadErr) {
                    console.error("Failed to upload image:", uploadErr);
                } finally {
                    setUploadingImage(false);
                }
            }

            const createImageUrl = resolveWorkspaceImageUrlForCreate(avatar, imageUrl);
            const response = (await workspacesApi.create({
                name: newWorkspaceName,
                storeId: newStoreId || undefined,
                ...(createImageUrl ? { imageUrl: createImageUrl } : {}),
            })) as { id?: string; data?: { id?: string }; workspace?: { id?: string } };

            const workspaceId =
                response?.id || response?.data?.id || response?.workspace?.id;

            if (workspaceId) {
                try {
                    await syncWorkspaceAvatarTags(workspaceId, avatar, []);
                } catch (tagErr) {
                    console.warn("Failed to set workspace avatar tags after create", tagErr);
                }
            }

            const createdName = newWorkspaceName.trim();
            showToast(
                createdName
                    ? `สร้าง "${createdName}" แล้ว`
                    : "สร้างเวิร์กสเปซแล้ว",
            );

            resetForm();
            if (onSuccess) onSuccess(workspaceId);
        } catch (err) {
            console.error("Failed to create workspace:", err);
            if (onError) onError("Failed to create workspace. Please try again.");
        } finally {
            setCreatingLoading(false);
        }
    };

    return {
        isCreating,
        setIsCreating,
        newWorkspaceName,
        setNewWorkspaceName,
        newStoreId,
        setNewStoreId,
        creatingLoading,
        avatar,
        setAvatar,
        uploadingImage,
        handleCreateWorkspace,
        resetForm,
    };
}
